import type * as AR from "adl-gen/adminui/api";
import {
  type CamelToSnakeCaseSkipFirst,
  camelToSnake,
  type SnakeToCamelCase,
  type WithPrefix,
} from "adl-gen/common/util/string";
import * as allTables from "adl-gen/ferovinum/app/db";
import { RESOLVER } from "adl-gen/resolver";
import type { ATypeExpr } from "adl-gen/runtime/adl";
import {
  type DbTableDetails,
  getAdlTableDetails,
  valueFromDbRow,
} from "utils/adl-db-tables";

const generalNameRx = /^[A-Z0-9-]+$/;

const accountCodeRx = /^[A-Z0-9-_]+$/;

const customValidationHooks: Partial<CustomValidationHooks> = {
  sale_order: {
    validate: async (saleOrder) => {
      if (saleOrder.orderStatusEvents.length > 0) {
        // Lookup for the cancelled event
        const cancelledEvent = saleOrder.orderStatusEvents.find(
          (event) => event.status === "canceled",
        );

        // Ensure that the cancelled event is the last entry if present
        if (cancelledEvent) {
          const lastEvent =
            saleOrder.orderStatusEvents[saleOrder.orderStatusEvents.length - 1];
          if (lastEvent.status !== "canceled") {
            return {
              orderStatusEvents:
                "The cancelled event must be the last entry in the list of order status events",
            };
          }
        }
      }
      return null;
    },
  },
  organisation: {
    validate: async (org) => {
      if (org.shortName && !generalNameRx.test(org.shortName)) {
        return {
          shortName:
            "Organisation short name should only contain capital letters, numbers and - ",
        };
      }
      return null;
    },
  },
  purchaser: {
    validate: async (purchaser) => {
      if (!accountCodeRx.test(purchaser.accountCode)) {
        return {
          accountCode:
            "Purchaser account code should only contain capital letters, numbers, hyphens (-), and underscores (_)",
        };
      }
      return null;
    },
  },
  storage_location: {
    validate: async (storageLocation) => {
      if (
        storageLocation.siteCode &&
        !generalNameRx.test(storageLocation.siteCode)
      ) {
        return {
          siteCode:
            "Storage location site code should only contain capital letters, numbers and - ",
        };
      }
      return null;
    },
  },
};

export async function rowValidation<T>(
  req: AR.CreateReq,
): Promise<ErrorOnColumn<T>> {
  const tableName = req.table as TableName;
  const validationHooks = customValidationHooks[
    tableName
  ] as CustomValidationHook<typeof tableName, T>;
  if (validationHooks) {
    if (!validationHooks.table) {
      //@ts-ignore FIX: The typing here as it is incorrect
      validationHooks.table = getAdlTableDetails(
        RESOLVER,
        allTexpr[tableName](),
      );
    }
    const obj = valueFromDbRow(
      validationHooks.table!,
      req.values as unknown as Record<string, unknown>,
    ).value;
    return validationHooks.validate(obj as T);
  }
  return null;
}

const AllTExpr: Pick<
  typeof allTables,
  keyof typeof allTables & `texpr${string}`
> = allTables;

const allTexpr: {
  [tableName in TableName]: (typeof allTables)[`texpr${SnakeToCamelCase<
    Capitalize<tableName>
  >}`];
} = Object.entries(allTables)
  .filter(([k]) => {
    return k.startsWith("texpr");
  })
  .reduce((acc, [k, v]) => {
    acc[camelToSnake(k.replace("texpr", "")) as TableName] = v;
    return acc;
  }, {} as any);

type TableName = CamelToSnakeCaseSkipFirst<
  WithPrefix<keyof typeof AllTExpr, "texpr">
>;

type CustomValidationHooks = {
  [tableName in TableName]: CustomValidationHook<tableName>;
};

export type ErrorOnColumn<T> = { [k in keyof Partial<T>]: string } | null;

interface CustomValidationHook<
  K extends TableName,
  T = (typeof allTexpr)[K] extends () => ATypeExpr<infer T> ? T : never,
> {
  table?: DbTableDetails<T>;
  validate: (row: T) => Promise<ErrorOnColumn<T>>;
}
